import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React, {useState, useEffect} from 'react';

import {PrimaryExperimentalRoleType} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {ExperimentalRole} from 'samples/experimental-roles/ExperimentalRoleConstants';
import {inferAnnotationParamsWithAi} from 'samples/plate-maps/annotate-plate-modal/ai-assistant/AnnotationAssistantDAO';
import AnnotationAssistantFeedback from 'samples/plate-maps/annotate-plate-modal/ai-assistant/AnnotationAssistantFeedback';
import {PlateMap, PlateMapWell} from 'samples/plate-maps/PlateMapConstants';
import {formatGridCoordinates} from 'samples/SampleUtil';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType, InlineBannerActionType} from 'ui-library/banners/InlineBanner';
import AsyncButton, {AsyncButtonLoadingBehavior} from 'ui-library/buttons/AsyncButton';
import Button, {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import Pill, {PillSize, PillStyle} from 'ui-library/buttons/Pill';
import {GRAY_DARKER} from 'ui-library/Colors';

export interface AnnotationAssistantInputProps {
  plateMap: PlateMap;
  onEditPlateMap: (
    plateMap: PlateMap,
    roleTrackingParams: {
      primaryRole?: PrimaryExperimentalRoleType;
      subrole?: string | null;
      annotatedByAi?: boolean;
    }
  ) => void;
  setPlateMapWithPreview: (plateMap: PlateMap | null) => void;
  setIsAIPreview: (isAIPreview: boolean) => void;
}
type WellWithRole = PlateMapWell & {role: ExperimentalRole};

export default function AnnotationAssistantInput({
  plateMap,
  onEditPlateMap,
  setPlateMapWithPreview,
  setIsAIPreview,
}: AnnotationAssistantInputProps): JSX.Element {
  const [userPrompt, setUserPrompt] = useState('');
  const [aiSuggestedPlateMap, setAISuggestedPlateMap] = useState<PlateMap | null>(null);
  const [suggestionTraceId, setSuggestionTraceId] = useState<string>('');

  useEffect(() => {
    setPlateMapWithPreview(aiSuggestedPlateMap);
    if (aiSuggestedPlateMap != null) {
      setIsAIPreview(true);
    } else {
      setIsAIPreview(false);
    }
  }, [aiSuggestedPlateMap, setIsAIPreview, setPlateMapWithPreview]);

  function applyAISuggestion(): void {
    if (aiSuggestedPlateMap == null) {
      return;
    }
    onEditPlateMap(aiSuggestedPlateMap, {annotatedByAi: true});
    setAISuggestedPlateMap(null);
  }

  return (
    <div>
      <div className={css(styles.title)}>
        <p className={css(styles.helpText)}>
          Describe role layout in plain English, and the AI assistant will suggest role annotations You can
          also paste in role annotations in any format and the AI assistant will map them for you.
        </p>
        <Pill size={PillSize.MINI} style={PillStyle.PRIMARY} icon="flask" text="BETA" />
      </div>
      <div className={css(styles.banner)}>
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text="This feature is powered by AI and may make mistakes."
          supportingInfo={
            <span>
              The AI system will attempt to annotate roles based on your input and existing roles. See the{' '}
              <a
                // TODO(BNCH-115938): Link to the real help doc when available.
                href="https://help.benchling.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                help article
              </a>{' '}
              for more details. For more information on AI security and privacy, look{' '}
              <a
                href="https://help.benchling.com/hc/en-us/articles/20901323667853-Security-and-privacy-for-LLMs-and-AI-powered-features"
                target="_blank"
                rel="noopener noreferrer"
              >
                here
              </a>
              .
            </span>
          }
          action={{actionType: InlineBannerActionType.ACCORDION}}
        />
      </div>
      <textarea
        className={css(styles.textarea)}
        placeholder="Describe your desired layout..."
        rows={5}
        value={userPrompt}
        onChange={(e) => {
          setUserPrompt(e.target.value);
        }}
      />
      <div className={css(styles.aiButtons)}>
        <AsyncButton
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.SMALL}
          onClick={async () => {
            const currentRoles = plateMap.wells
              .filter((well): well is WellWithRole => well.role != null)
              .reduce((acc: {[coord: string]: ExperimentalRole}, well) => {
                acc[formatGridCoordinates(well.position.row, well.position.column)] = well.role;
                return acc;
              }, {});
            const res = await inferAnnotationParamsWithAi({
              currentRoles,
              dimensions: {rows: plateMap.size.numRows, columns: plateMap.size.numColumns},
              userPrompt,
            });
            setSuggestionTraceId(res.traceId);
            if (res.error != null) {
              notifications.error(res.error);
              return;
            }
            const updatedPlateMap = produce(plateMap, (draft) => {
              draft.wells.forEach((well) => {
                const pos = formatGridCoordinates(well.position.row, well.position.column);
                well.role = res.roleAnnotations[pos];
              });
              return draft;
            });
            setAISuggestedPlateMap(updatedPlateMap);
          }}
          text={aiSuggestedPlateMap == null ? 'Suggest roles' : 'Resuggest roles'}
          loadingBehavior={AsyncButtonLoadingBehavior.SPINNER}
          disabled={userPrompt === ''}
        />
      </div>
      <div className={css(styles.aiButtons)}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.SMALL}
          text="Discard suggestion"
          disabled={aiSuggestedPlateMap == null}
          onClick={() => {
            setAISuggestedPlateMap(null);
          }}
        />
        <Button
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.SMALL}
          text="Apply suggestion"
          disabled={aiSuggestedPlateMap == null}
          onClick={() => {
            applyAISuggestion();
          }}
        />
      </div>
      {aiSuggestedPlateMap != null && (
        <AnnotationAssistantFeedback userPrompt={userPrompt} traceId={suggestionTraceId} />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  title: {
    display: 'flex',
    gap: 8,
  },
  helpText: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
  },
  banner: {
    marginBottom: 8,
  },
  textarea: {
    resize: 'none',
    marginBottom: 8,
  },
  aiButtons: {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
    marginBottom: 16,
  },
});
